import React, { useEffect, useState } from "react";
import { Headphones, BellSimple, ChatCircleDots, PaperPlaneTilt, Check } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

function formatCount(n) {
  if (!n) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return n.toLocaleString();
}

export function PlayCounter({ count }) {
  return (
    <div
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-ink-800/60 border border-neon-cyan/30"
      data-testid="play-counter"
      title={`${count.toLocaleString()} total plays`}
    >
      <Headphones size={14} weight="fill" className="text-neon-cyan animate-pulse" />
      <span className="font-mono text-sm font-bold tabular-nums text-white">{formatCount(count)}</span>
      <span className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 font-bold">plays</span>
    </div>
  );
}

export function SubscribeBox({ rank, artistName, initialCount }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [optShoow, setOptShoow] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [count, setCount] = useState(initialCount || 0);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/slots/${rank}/subscribe`, { email, opt_in_artist: true, opt_in_shoow: optShoow });
      toast.success(data.message || "Subscribed!");
      setDone(true);
      setCount((c) => c + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-ink-600 bg-ink-800/40 p-3" data-testid={`subscribe-box-${rank}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3"
        data-testid={`subscribe-toggle-${rank}`}
      >
        <span className="flex items-center gap-2 text-sm text-zinc-200">
          <BellSimple size={14} weight="fill" className="text-neon-cyan" />
          Subscribe to <strong className="text-white">{artistName}</strong>
        </span>
        <span className="font-mono text-xs text-neon-cyan">{count} subs</span>
      </button>
      {open && !done && (
        <form onSubmit={submit} className="mt-3 space-y-2" data-testid={`subscribe-form-${rank}`}>
          <input
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className="w-full px-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-white text-sm focus:outline-none focus:border-neon-cyan/60"
            data-testid={`subscribe-email-${rank}`}
          />
          <label className="flex items-center gap-2 text-xs text-zinc-400">
            <input type="checkbox" checked={optShoow} onChange={(e) => setOptShoow(e.target.checked)} />
            Also send me Undaground Shoow updates
          </label>
          <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-neon-cyan text-black font-semibold text-xs hover:bg-neon-cyanHover transition-colors disabled:opacity-60" data-testid={`subscribe-submit-${rank}`}>
            <PaperPlaneTilt size={12} weight="bold" />
            {busy ? "Subscribing..." : `Subscribe to ${artistName}`}
          </button>
          <p className="text-[10px] text-zinc-600">The artist can reach you through Undaground Shoow. Unsubscribe anytime.</p>
        </form>
      )}
      {done && (
        <p className="mt-2 text-xs text-neon-cyan inline-flex items-center gap-1.5" data-testid={`subscribe-done-${rank}`}>
          <Check size={12} weight="bold" /> Subscribed — the artist will reach out via the Shoow.
        </p>
      )}
    </div>
  );
}

export function CommentsBox({ rank }) {
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/slots/${rank}/comments`);
      setComments(data || []);
      setLoaded(true);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("Failed to load comments:", err?.message);
      }
    }
  };

  useEffect(() => { if (open && !loaded) load(); }, [open]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/slots/${rank}/comments`, { name, comment: text });
      setComments((c) => [data, ...c]);
      setText("");
      toast.success("Comment posted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-ink-600 bg-ink-800/40 p-3" data-testid={`comments-box-${rank}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-3" data-testid={`comments-toggle-${rank}`}>
        <span className="flex items-center gap-2 text-sm text-zinc-200">
          <ChatCircleDots size={14} weight="fill" className="text-neon-cyan" />
          Leave a comment
        </span>
        <span className="font-mono text-xs text-zinc-500">{comments.length || ""}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <form onSubmit={submit} className="space-y-2" data-testid={`comment-form-${rank}`}>
            <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Your name" className="w-full px-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-white text-sm focus:outline-none focus:border-neon-cyan/60" data-testid={`comment-name-${rank}`} />
            <textarea value={text} onChange={(e) => setText(e.target.value)} required rows={2} placeholder="Drop a comment for the artist..." className="w-full px-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-white text-sm focus:outline-none focus:border-neon-cyan/60 resize-y" data-testid={`comment-text-${rank}`} />
            <button type="submit" disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-neon-cyan text-black font-semibold text-xs hover:bg-neon-cyanHover transition-colors disabled:opacity-60" data-testid={`comment-submit-${rank}`}>
              <PaperPlaneTilt size={12} weight="bold" /> Post
            </button>
          </form>
          {comments.length > 0 && (
            <ul className="space-y-2 max-h-60 overflow-y-auto scrollbar-thin pt-2 border-t border-ink-700">
              {comments.map((c) => (
                <li key={c.id} className="text-xs">
                  <p className="text-neon-cyan font-mono">{c.name}</p>
                  <p className="text-zinc-300 leading-relaxed">{c.comment}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
